import { useState, useEffect, useCallback } from 'react';
import { TableGameConfig } from '../types/tableGame';
import { logger } from '../utils/logger';

const STORAGE_KEY = 'table_game_config';

interface UseTableGamePersistence {
  config: TableGameConfig | null;
  saveConfig: (config: Partial<TableGameConfig>) => void;
  clearConfig: () => void;
  initializeConfig: () => TableGameConfig;
}

export const useTableGamePersistence = (): UseTableGamePersistence => {
  const [config, setConfig] = useState<TableGameConfig | null>(null);

  // Load config from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setConfig(parsed);
      }
    } catch (error) {
      logger.error('Error loading table game config:', error);
    }
  }, []);

  // Save config to localStorage and state
  const saveConfig = useCallback((updates: Partial<TableGameConfig>) => {
    setConfig(prev => {
      const newConfig = prev ? { ...prev, ...updates } : initializeDefaultConfig();
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newConfig));
      } catch (error) {
        logger.error('Error saving table game config:', error);
      }
      return newConfig;
    });
  }, []);

  // Clear config from localStorage and state
  const clearConfig = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      setConfig(null);
    } catch (error) {
      logger.error('Error clearing table game config:', error);
    }
  }, []);

  // Initialize a new config
  const initializeConfig = useCallback((): TableGameConfig => {
    const newConfig = initializeDefaultConfig();
    saveConfig(newConfig);
    return newConfig;
  }, [saveConfig]);

  return {
    config,
    saveConfig,
    clearConfig,
    initializeConfig
  };
};

// Helper function to create default config
function initializeDefaultConfig(): TableGameConfig {
  return {
    tableId: crypto.randomUUID(),
    playerCount: 3,
    players: Array.from({ length: 3 }, (_, i) => ({
      id: crypto.randomUUID(),
      name: `Jugador ${i + 1}`,
      order: i
    })),
    wordMode: 'random',
    impostorMode: 'normal',
    impostorCount: 1
  };
}
